import React from 'react'

import Input, { CalculatedInput } from '../ui/input'
import FieldLabel from '../ui/input/FieldLabel'
import TextArea from '../ui/input/textArea'
import Button, { ButtonSize, ButtonVariant } from '../ui/button'
import { Currency, InvoiceItem } from '../../types'
import type { ItemFieldKey, ItemValidationErrors } from './form'
import IconButton from '../ui/button/icon'
import { ReactComponent as TrashIcon } from '../../static/images/trash.svg'
import { ReactComponent as PlusIcon } from '../../static/images/plus.svg'
import * as utils from '../../utils'
import './invoiceItemsStyles.scss'

const DESCRIPTION_MAX_ROWS = 6

const isZeroRate = (rate: InvoiceItem['rate']): boolean =>
  rate != null && rate !== '' && Number(rate) === 0

const isDefaultQuantity = (quantity: InvoiceItem['quantity']): boolean =>
  quantity === 1 || quantity === '1'

const makeRateChangeEvent = (index: number, value: string): React.ChangeEvent<HTMLInputElement> =>
  ({ target: { name: `invoiceItemRate${index}`, value } } as React.ChangeEvent<HTMLInputElement>)

const makeQuantityChangeEvent = (index: number, value: string): React.ChangeEvent<HTMLInputElement> =>
  ({ target: { name: `invoiceItemQuantity${index}`, value } } as React.ChangeEvent<HTMLInputElement>)

type Props = {
  onItemChange: React.ChangeEventHandler<HTMLInputElement | HTMLTextAreaElement>
  addNewItem: () => void
  removeItem: (index: number) => void
  values: {
    items: InvoiceItem[]
    total: string | number | undefined
    totalTax: string | number | undefined
  }
  taxLabel?: string
  currency?: Currency
  enableTax?: boolean
  disabled?: boolean
  itemFieldErrors?: ItemValidationErrors
  itemsEmptyError?: boolean
  itemsFlashNonce?: number
}

const focusVisibleDescriptionField = (index: number) => {
  const textareas = document.getElementsByName(`invoiceItemDescription${index}`)
  const visible = Array.from(textareas).find(
    (el) => (el as HTMLElement).offsetParent !== null
  ) as HTMLTextAreaElement | undefined
  visible?.focus()
}

const InvoiceItems: React.FC<Props> = ({ disabled, enableTax, taxLabel, values, currency, addNewItem, removeItem, onItemChange, itemFieldErrors, itemsEmptyError, itemsFlashNonce }) => {
  const [focusDescriptionIndex, setFocusDescriptionIndex] = React.useState<number | null>(null)

  React.useEffect(() => {
    if (focusDescriptionIndex == null) return
    focusVisibleDescriptionField(focusDescriptionIndex)
    setFocusDescriptionIndex(null)
  }, [focusDescriptionIndex, values.items.length])

  const fieldErrorClass = (index: number, field: ItemFieldKey) =>
    itemFieldErrors?.[index]?.[field] ? 'is-invalid' : ''
  const onAddNewItem = (e: any) => {
    e.preventDefault()
    e.stopPropagation()
    setFocusDescriptionIndex(values.items.length)
    addNewItem()
  }

  const onRemoveItem = (e: any, index: number) => {
    e.preventDefault()
    e.stopPropagation()
    removeItem(index)
  }

  const onRateFocus = (index: number, rate: InvoiceItem['rate']) => () => {
    if (isZeroRate(rate)) {
      onItemChange(makeRateChangeEvent(index, ''))
    }
  }

  const onRateBlur = (index: number, rate: InvoiceItem['rate']) => () => {
    if (rate === '' || rate == null) {
      onItemChange(makeRateChangeEvent(index, '0'))
    }
  }

  const onQuantityFocus = (index: number, quantity: InvoiceItem['quantity']) => () => {
    if (isDefaultQuantity(quantity)) {
      onItemChange(makeQuantityChangeEvent(index, ''))
    }
  }

  const onQuantityBlur = (index: number, quantity: InvoiceItem['quantity']) => () => {
    if (quantity === '' || quantity == null) {
      onItemChange(makeQuantityChangeEvent(index, '1'))
    }
  }

  const hasItems = values.items && values.items.length > 0
  const itemCount = values.items?.length ?? 0

  return (
    <div data-invoice-field='items'>
      <div>
          <div className='invoice-items-header d-flex justify-content-between align-items-baseline'>
            <FieldLabel className='d-md-none' label='Line Items' required />
            {hasItems && (
              <span className='invoice-items-header__count d-md-none'>
                {itemCount} line item{itemCount === 1 ? '' : 's'}
              </span>
            )}
          </div>
          {!hasItems && (
            <>
              <EmptyInvoiceItems
                onAdd={onAddNewItem}
                disabled={disabled}
                hasError={itemsEmptyError}
                flashNonce={itemsFlashNonce}
              />
            </>
          )}
          {hasItems && (
          <div className='row py-2 d-none d-md-flex'>
            <div className='col'><FieldLabel label='Description' required /></div>
            <div className='col-1 col-sm-1 invoice-items-qty-col'><FieldLabel label='Qty' required /></div>
            <div className='col-2 col-sm-2'><FieldLabel label={<>{'Rate '}<small>{currency && `(${currency.symbol})`}</small></>} required /></div>
            {enableTax && <div className='col-sm-1 g-0'><FieldLabel label={`${taxLabel} (%)`} /></div>}
            <div className='col-2 col-sm-2 text-end'><FieldLabel label={<>{'Amount '}<small>{currency && `(${currency.symbol})`}</small></>} /></div>
            <div className='col-1' />
          </div>
          )}
          {hasItems && values.items.map((item, index) => {
            const descriptionFieldProps = {
              autoComplete: 'off' as const,
              value: item.description ?? '',
              name: `invoiceItemDescription${index}`,
              onChange: onItemChange,
              disabled,
              required: !disabled,
              autoGrow: true,
              maxRows: DESCRIPTION_MAX_ROWS,
              className: `invoice-item-description ${fieldErrorClass(index, 'description')}`.trim(),
            }
            const rateFieldProps = {
              type: 'number' as const,
              step: '0.01',
              value: item.rate,
              name: `invoiceItemRate${index}`,
              onChange: onItemChange,
              onFocus: onRateFocus(index, item.rate),
              onBlur: onRateBlur(index, item.rate),
              disabled,
              className: fieldErrorClass(index, 'rate'),
            }
            const quantityFieldProps = {
              type: 'number' as const,
              min: '0',
              step: '.25',
              value: item.quantity,
              name: `invoiceItemQuantity${index}`,
              onChange: onItemChange,
              onFocus: onQuantityFocus(index, item.quantity),
              onBlur: onQuantityBlur(index, item.quantity),
              disabled,
              className: fieldErrorClass(index, 'quantity'),
            }
            return (
            <div key={index} id={`invoice-item-${index}`}>
              {/* INV ITEM DESKTOP VIEW */}
              <div className='row py-1 d-none d-md-flex mb-1'>
                <div className='col'>
                  <TextArea {...descriptionFieldProps} />
                </div>
                <div className='col-1 col-sm-1 invoice-items-qty-col'>
                  <Input {...quantityFieldProps} />
                </div>
                <div className='col-2 col-sm-2'>
                  <Input {...rateFieldProps} />
                </div>
                {enableTax && (
                  <div className='col-1 g-0'>
                    <Input
                      type='number'
                      step='1'
                      defaultValue={item.taxPercentage && parseFloat(item.taxPercentage.toString())}
                      value={item.taxPercentage && parseFloat(item.taxPercentage.toString())}
                      name={`invoiceItemTaxPercentage${index}`}
                      onChange={onItemChange}
                      disabled={disabled}
                    />
                  </div>
                )}

                <div className='col-2 col-sm-2'>
                  <CalculatedInput
                    type='number'
                    step='0.01'
                    value={utils.formatItemAmountForDisplay(item)}
                    name={`invoiceItemAmount${index}`}
                    className='invoice-item-amount-value'
                    disabled
                    readOnly
                    align='end'
                  />
                </div>

                <div className='col-1 w-1 p-0 pt-1 d-flex align-items-start'>
                  {!disabled && (
                    <IconButton icon={<TrashIcon width={20} />} onClick={(e: any) => onRemoveItem(e, index)}  />
                  )}
                </div>
              </div>

              {/* INV ITEM MOBILE VIEW */}
              <div className='d-md-none border rounded-4 p-3 mb-3 invoice-item-mobile'>
                {!disabled && (
                  <div className='invoice-item-mobile__remove'>
                    <IconButton icon={<TrashIcon width={22} />} onClick={(e: any) => onRemoveItem(e, index)} />
                  </div>
                )}
                <div>
                  <TextArea
                    label='Description'
                    id={`invoiceItemDescription${index}`}
                    {...descriptionFieldProps}
                    minRows={2}
                  />
                </div>
                <div className='mt-2'>
                  <Input
                    label='Qty'
                    required
                    {...quantityFieldProps}
                  />
                </div>
                <div className='mt-2'>
                  <Input
                    label={`Rate${currency ? ` (${currency.symbol})` : ''}`}
                    required
                    {...rateFieldProps}
                  />
                </div>
                {enableTax && (
                  <div className='mt-2'>
                    <Input
                      label={taxLabel ? `${taxLabel} (%)` : 'Tax (%)'}
                      type='number'
                      step='1'
                      defaultValue={item.taxPercentage && parseFloat(item.taxPercentage.toString())}
                      value={item.taxPercentage && parseFloat(item.taxPercentage.toString())}
                      name={`invoiceItemTaxPercentage${index}`}
                      onChange={onItemChange}
                      disabled={disabled}
                    />
                  </div>
                )}
                <div className='mt-2'>
                  <CalculatedInput
                    label={`Amount ${currency ? ` (${currency.symbol})` : ''}`}
                    type='number'
                    step='0.01'
                    value={utils.formatItemAmountForDisplay(item)}
                    name={`invoiceItemAmount${index}`}
                    disabled
                    readOnly
                    singleLine
                    className='invoice-item-amount-value'
                  />
                </div>
              </div>
            </div>
            )
          })}
          {hasItems && !disabled && <ItemActions onAddNewItem={onAddNewItem} disabled={disabled} />}
      </div>
    </div>
  )
}

type EmptyInvoiceItemsProps = {
  onAdd: (e: any) => void
  disabled?: boolean
  hasError?: boolean
  flashNonce?: number
}

const EmptyInvoiceItems: React.FC<EmptyInvoiceItemsProps> = ({ onAdd, disabled, hasError, flashNonce }) => (
  <div
    key={flashNonce}
    className={`invoice-items-empty position-relative ${hasError ? 'invoice-items-empty--error invoice-items-empty--flash' : ''}`}
  >
    <p className='invoice-items-empty__description'>Add line item for this invoice</p>
    {!disabled && (
      <Button
        icon={<PlusIcon width={14} height={14} />}
        iconPosition='start'
        size={ButtonSize.AUTO}
        variant={ButtonVariant.LIGHT}
        type='button'
        onClick={onAdd}
      >
        Add Line Item
      </Button>
    )}
    {hasError && <small className='text-danger position-absolute start-50 translate-middle-x bottom-0 pb-2'>Required</small>}
  </div>
)

type ActionProps = {
  onAddNewItem: (e: any) => void
  disabled?: boolean
}
const ItemActions: React.FC<ActionProps> = ({ onAddNewItem, disabled }) => {
  return (
    <div className='row'>
      <div className='col col-lg-3'>
        <Button icon={<PlusIcon width={14} height={14} />} iconPosition='start' size={ButtonSize.SMALL} variant={ButtonVariant.LIGHT} type='button' onClick={onAddNewItem} disabled={disabled}>New line item</Button>
      </div>
    </div>
  )
}

export default InvoiceItems
